import { memo, useMemo } from 'react';
import {
  useNavigation,
  StackNavigationProp,
  BottomSheet,
} from '@betty/navigation';
import { ScrollView, Text } from '@betty/moonlight';
import { Payment } from '../../state/payment/payment.types';
import { getPayments } from '../Payment/Payment.utils';
import { PAYMENTS_CONFIG } from './WithdrawPayments.content';
import { WithdrawNavigatorParamsList } from '../../navigators/WithdrawNavigator/WithdrawNavigator.types';
import usePaymentProviders from '../../hooks/usePaymentProviders';
import PaymentNotAvailable from '../PaymentNotAvailable/PaymentNotAvailable';
import PaymentComponent from '../Payment/Payment';
import WithdrawPaymentsSkeleton from './WithdrawPaymentsSkeleton';
import useWithdrawSettings from '../../hooks/useWithdrawSettings';

function WithdrawPayments() {
  const navigation =
    useNavigation<StackNavigationProp<WithdrawNavigatorParamsList>>();
  const { data: providers } = usePaymentProviders('withdrawals');
  const { data: settings } = useWithdrawSettings();
  const payments = useMemo(() => getPayments(providers), [providers]);
  const handlers = useMemo(
    () => ({
      [Payment.Visa]: () => navigation.navigate('WithdrawLaunchScreen'),
      [Payment.Interac]: () =>
        BottomSheet.open({
          namespace: 'wallet',
          type: 'WithdrawInterac',
          titleNS: 'wallet',
          titleKey: 'paymentInteracEtransferTitle',
          data: {
            minAmount: settings?.minAmount,
            maxAmount: settings?.providerMaxAmount.Gigadat,
          },
          scrollable: true,
        }),
      [Payment.ApplePay]: () => navigation.navigate('WithdrawLaunchScreen'),
    }),
    [settings, navigation],
  );

  if (!providers || !settings) {
    return <WithdrawPaymentsSkeleton />;
  }

  if (!providers.length) {
    return <PaymentNotAvailable />;
  }

  return (
    <ScrollView
      contentContainerStyle={{
        gap: 'space-400',
        paddingTop: 'space-400',
        paddingHorizontal: 'space-400',
      }}
    >
      <Text
        variant="body-m"
        locNS="wallet"
        locKey="withdrawPaymentsTitle"
        color="darkSurfacePrimary"
      />
      {payments?.map(
        (payment) =>
          payment !== Payment.Mastercard && (
            <PaymentComponent
              key={payment}
              onPress={handlers[payment]}
              {...PAYMENTS_CONFIG[payment]}
            />
          ),
      )}
    </ScrollView>
  );
}

export default memo(WithdrawPayments);
